import React, { useEffect, useMemo, useState } from "react"
import { Button, Typography } from "@fullscreendigital/carrefour-dex-fe"
import { Table } from "components/core-ui/Table"
import { CircularProgress } from "@mui/material"
import { useNotification } from "../../../hooks"
import { formatErrorMessage } from "utils/formatting"
import { useNavigate } from "react-router-dom"
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import 'dayjs/locale/ro'

import s from "../../shop-reception/commonStyles.modules.scss"
import { EmployeeCashbackUiRow } from "../interfaces"
import { fetchEmployeesProgress } from "../api"
import { columns, toTableRowItems, toolbarActions, toUiRows, drawerFilters } from "./helpers"
import { isValidYearMonth } from "../utils"
import { makeCoreGetFileRequest } from "../../../utils/api"
import { formatAndDownloadResponse } from "../../../utils/helpers"

const getDefaultMonth = () => {
  const today = new Date()
  const year = today.getFullYear()
  const month = today.getMonth() + 1
  const mm = month.toString().padStart(2, "0")

  return `${year}-${mm}`
}

const CashBackReportPage: React.FC = () => {
  const navigate = useNavigate()
  const { showNotification } = useNotification()

  const [month, setMonth] = useState<string>("")
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const [tableItems, setTableItems] = useState<any[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [lastLoadedRows, setLastLoadedRows] = useState<EmployeeCashbackUiRow[]>([])

  const hasData = lastLoadedRows.length > 0

  const monthValue: Dayjs | null = useMemo(() => {
    if (!month || !isValidYearMonth(month)) return null
    return dayjs(`${month}-01`)
  }, [month])

  const handleMonthChange = (val: Dayjs | null) => {
    if (!val || !val.isValid()) {
      setMonth('')
      return
    }
    setMonth(val.format('YYYY-MM'))
  }

  const totalEligible = useMemo(
    () => lastLoadedRows.reduce((sum: number, r: any) => sum + (r.totalEligibleAmount ?? 0), 0),
    [lastLoadedRows]
  )
  const totalCashback = useMemo(
    () => lastLoadedRows.reduce((sum: number, r: any) => sum + (r.cashbackAmount ?? 0), 0),
    [lastLoadedRows]
  )

  const fetchData = async ({ page, rowsPerPage, tableFilters }: any) => {
    if (!month) return

    if (!isValidYearMonth(month)) {
      showNotification({
        type: "error",
        message: "Luna trebuie să fie în format YYYY-MM (ex: 2026-02).",
      })
      return
    }

    const [yStr, mStr] = month.split("-")
    const year = Number(yStr)
    const monthNo = Number(mStr)

    setIsLoading(true)
    try {
      const res = await fetchEmployeesProgress({
        year,
        month: monthNo,
        page,
        size: rowsPerPage,
        tableFilters
      } as any)

      const list = res.list ?? []
      const uiRows: EmployeeCashbackUiRow[] = toUiRows(list)

      setLastLoadedRows(uiRows)
      setTableItems(toTableRowItems(uiRows))
      setTotalCount(res.page?.totalCount ?? uiRows.length)
    } catch (error) {
      showNotification({
        type: "error",
        message: `Eroare la încărcarea raportului. ${formatErrorMessage(error)}`,
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const m = getDefaultMonth()
    setMonth(m)
    setTimeout(() => setShouldRefreshData(true), 0)
  }, [])

  const handleSearch = () => {
    if (!month) return

    if (!isValidYearMonth(month)) {
      showNotification({
        type: "error",
        message: "Luna trebuie să fie în format YYYY-MM (ex: 2026-02).",
      })
      return
    }

    setShouldRefreshData(true)
    showNotification({ type: "info", message: `Se încarcă raportul pentru ${month}...` })
  }

  const handleExport = async () => {
    if (!hasData) return

    if (!month || !isValidYearMonth(month)) {
      showNotification({
        type: "error",
        message: "Luna trebuie să fie în format YYYY-MM (ex: 2026-02).",
      })
      return
    }

    const [yStr, mStr] = month.split("-")
    const year = Number(yStr)
    const monthNo = Number(mStr)

    setIsExporting(true)
    try {
      const response = await makeCoreGetFileRequest(`/employee-discount/download?year=${year}&month=${monthNo}`, { responseType: 'blob' })
      formatAndDownloadResponse(response)
      showNotification({ type: "success", message: "Exportul pentru Payroll a fost pregătit." })
    } catch (error) {
      showNotification({
        type: "error",
        message: `A apărut o eroare la export. ${formatErrorMessage(error)}`,
      })
    } finally {
      setIsExporting(false)
    }
  }

  const toolbar = useMemo(
    () =>
      toolbarActions({
        onRefresh: () => setShouldRefreshData(true),
        isLoading,
        handleExport,
        isExporting,
        canExport: hasData && !isExporting && !isLoading,
      }),
    [isLoading, isExporting, month, hasData]
  )

  return (
    <div className={s.mainContainer}>
      <Typography variant="medium" component="h2" style={{ textAlign: "center" }}>
        Raport cashback angajați
      </Typography>

      <div style={{ marginTop: 30 }}>
        <Table
          hideCheckbox
          columns={columns({
            onDetails: (row: any) => {
              const [yStr, mStr] = month.split("-")
              navigate(`/employeeDiscounts/cashbackReport/${row.afgCardId}?year=${yStr}&month=${Number(mStr)}`)
            }
          })}
          items={tableItems}
          fetchData={fetchData}
          isLoading={isLoading}
          totalCount={totalCount}
          shouldRefreshData={shouldRefreshData}
          setShouldRefreshData={setShouldRefreshData}
          toolbarActions={toolbar}
          filters={drawerFilters()}
          filtersButtonLabel="Filtre"
          filtersLabel="Filtre"
          appliedFiltersLabel="Filtre aplicate"
          cancelFiltersButtonLabel="Anuleaza"
          applyFiltersButtonLabel="Aplica"
          rowActionsWidth="120px"
          rowsPerPage={10}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </div>

      <div style={{ marginTop: 24, textAlign: "center", fontWeight: 500 }}>
        {hasData ? (
          <>
            Pentru luna <b>{month}</b>, total eligibil: <b>{totalEligible.toFixed(2)} RON</b>, total cashback:{" "}
            <b>{totalCashback.toFixed(2)} RON</b> pentru {lastLoadedRows.length} angajați.
          </>
        ) : (
          <span style={{ color: "#666" }}>Nu există date încă pentru luna selectată.</span>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(260px, 340px) auto auto",
          gap: "16px",
          alignItems: "end",
          marginTop: 45,
          maxWidth: 920,
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ro">
          <DatePicker
            label="Lună"
            views={['year', 'month']}
            openTo="month"
            value={monthValue}
            onChange={handleMonthChange}
            format="YYYY-MM"
            slotProps={{
              textField: {
                variant: 'standard',
                fullWidth: true,
                helperText: 'Selectează luna și anul',
              },
            }}
          />
        </LocalizationProvider>

        <Button
          type="button"
          variant="contained"
          label="CAUTĂ"
          onClick={handleSearch}
          disabled={!month || isLoading}
          style={{ minWidth: 140, height: 40 }}
        >
          {isLoading && <CircularProgress size={20} color="inherit" style={{ marginRight: 8 }} />}
        </Button>
      </div>
    </div>
  )
}

export default CashBackReportPage
